import {
    EDIT_PERMISSION_WILDCARD,
    UPDATE_DEVICE_CONFIG_CONTAINER_FIELDS,
    UPDATE_DEVICE_EDIT_PERMISSION_TIERS,
    UPDATE_DEVICE_SECTIONS,
} from './updateDeviceEditPermissions.config'

const cloneData = data =>
    data === undefined ? undefined : JSON.parse(JSON.stringify(data))

const hasWildcard = values =>
    Array.isArray(values) && values.includes(EDIT_PERMISSION_WILDCARD)

const hasEditableFields = rule =>
    Array.isArray(rule?.fields) && rule.fields.length > 0

const hasAllFields = rule =>
    Array.isArray(rule?.fields) && hasWildcard(rule.fields)

const hasExcludedField = (rule, fieldKey) =>
    Array.isArray(rule?.exclude_fields) && rule.exclude_fields.includes(fieldKey)

const getSectionRuleInfo = (permission, sectionKey) => {
    const sections = permission?.sections ?? {}

    if (hasAllFields(sections[EDIT_PERMISSION_WILDCARD])) {
        return {
            isGlobal: true,
            rule: sections[EDIT_PERMISSION_WILDCARD],
        }
    }

    return {
        isGlobal: false,
        rule: sections[sectionKey],
    }
}

const getSectionRule = (permission, sectionKey) => {
    return getSectionRuleInfo(permission, sectionKey).rule
}

const getContainerRule = (sectionRule, containerKey) => {
    const containers = sectionRule?.containers ?? {}
    if (hasAllFields(containers[EDIT_PERMISSION_WILDCARD])) {
        return containers[EDIT_PERMISSION_WILDCARD]
    }

    return containers[containerKey]
}

const isFieldAllowedByRule = (rule, fieldKey) => {
    if (!Array.isArray(rule?.fields)) return false
    if (hasExcludedField(rule, fieldKey)) return false

    return hasWildcard(rule.fields) || rule.fields?.includes(fieldKey)
}

const canUseSectionFields = sectionKey =>
    sectionKey !== UPDATE_DEVICE_SECTIONS.config

export const resolveUpdateDeviceEditPermission = isEdit =>
    isEdit
        ? UPDATE_DEVICE_EDIT_PERMISSION_TIERS.full
        : UPDATE_DEVICE_EDIT_PERMISSION_TIERS.limited

export const canEditSection = (sectionKey, permission) => {
    const {isGlobal, rule: sectionRule} = getSectionRuleInfo(
        permission,
        sectionKey,
    )

    if (!sectionRule) return false

    return (
        isGlobal ||
        Object.keys(sectionRule.containers ?? {}).length > 0 ||
        (canUseSectionFields(sectionKey) && hasEditableFields(sectionRule))
    )
}

export const canEditContainer = (sectionKey, containerKey, permission) => {
    const {isGlobal, rule: sectionRule} = getSectionRuleInfo(
        permission,
        sectionKey,
    )
    const containerRule = getContainerRule(sectionRule, containerKey)

    if (isGlobal || hasAllFields(containerRule)) return true
    if (!containerRule) return false

    return hasEditableFields(containerRule)
}

export const canEditField = (
    sectionKey,
    fieldKey,
    permission,
    containerKey,
) => {
    const {isGlobal, rule: sectionRule} = getSectionRuleInfo(
        permission,
        sectionKey,
    )

    if (!sectionRule) return false
    if (isGlobal) return isFieldAllowedByRule(sectionRule, fieldKey)
    if (
        canUseSectionFields(sectionKey) &&
        isFieldAllowedByRule(sectionRule, fieldKey)
    ) {
        return true
    }

    if (!containerKey) return false

    const containerRule = getContainerRule(sectionRule, containerKey)

    return isFieldAllowedByRule(containerRule, fieldKey)
}

const resolveConfigContainerByField = fieldKey =>
    Object.entries(UPDATE_DEVICE_CONFIG_CONTAINER_FIELDS).find(([, fields]) =>
        fields.includes(fieldKey),
    )?.[0]

const getEditableSectionData = (
    sectionKey,
    formSectionData = {},
    initialSectionData = {},
    permission,
) => {
    const {isGlobal, rule: sectionRule} = getSectionRuleInfo(
        permission,
        sectionKey,
    )

    if (
        (isGlobal || canUseSectionFields(sectionKey)) &&
        hasAllFields(sectionRule) &&
        !Array.isArray(sectionRule?.exclude_fields)
    ) {
        return cloneData(formSectionData)
    }

    return Object.keys(formSectionData).reduce(
        (nextSectionData, fieldKey) => {
            const containerKey =
                sectionKey === 'config'
                    ? resolveConfigContainerByField(fieldKey)
                    : undefined

            if (canEditField(sectionKey, fieldKey, permission, containerKey)) {
                nextSectionData[fieldKey] = formSectionData[fieldKey]
            }

            return nextSectionData
        },
        cloneData(initialSectionData),
    )
}

export const filterEditablePayload = (
    formData,
    initialFormData,
    permission,
) => {
    const globalRule = permission?.sections?.[EDIT_PERMISSION_WILDCARD]

    if (hasAllFields(globalRule) && !Array.isArray(globalRule?.exclude_fields)) {
        return cloneData(formData)
    }

    return Object.keys(initialFormData).reduce(
        (payload, sectionKey) => ({
            ...payload,
            [sectionKey]: getEditableSectionData(
                sectionKey,
                formData?.[sectionKey],
                initialFormData?.[sectionKey],
                permission,
            ),
        }),
        cloneData(initialFormData),
    )
}

export const restoreEditableFields = (
    currentFormData,
    initialFormData,
    permission,
) => {
    const globalRule = permission?.sections?.[EDIT_PERMISSION_WILDCARD]

    if (hasAllFields(globalRule) && !Array.isArray(globalRule?.exclude_fields)) {
        return cloneData(initialFormData)
    }

    return filterEditablePayload(initialFormData, currentFormData, permission)
}
